import React, { useState, useEffect } from "react";
import {
  GitBranch,
  Plus,
  Search,
  Trash2,
  Save,
  ChevronRight,
  AlertCircle,
  CheckCircle2,
  FileCode2,
  Layers,
  ChevronLeft
} from "lucide-react";
import api from "../utils/api";
import PageHeader from "../components/PageHeader";
import { useToast } from "../context/ToastContext";
import DeleteConfirmModal from "../components/DeleteConfirmModal";

const BusinessFlows = ({ activeAgentId }) => {
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeFlow, setActiveFlow] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const { showToast } = useToast();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [flowToDelete, setFlowToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const FLOWS_PER_PAGE = 10;

  const fetchFlows = React.useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getFlows();
      setFlows(data || []);
      setLoading(false);
    } catch (error) {
      showToast("Error fetching business flows", "error");
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchFlows();
  }, [activeAgentId, fetchFlows]);

  // Removed local showToast to use global useToast

  const handleSelectFlow = async (flow) => {
    try {
      setLoading(true);
      const detail = await api.getFlowDetail(flow.id);
      setActiveFlow(detail);
      setEditMode(false);
      setLoading(false);
    } catch (error) {
      showToast("Error loading neural sequence details", "error");
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!activeFlow.name || !activeFlow.content) {
      showToast("Name and Content are required", "error");
      return;
    }

    try {
      if (activeFlow.id) {
        // Update existing
        await api.updateFlow(activeFlow.id, {
          name: activeFlow.name,
          content: activeFlow.content,
          description: activeFlow.description || "",
          is_active: activeFlow.is_active
        });
        showToast("Sequence synchronized successfully");
      } else {
        // Create new
        await api.saveFlow({
          ...activeFlow,
          agent_id: activeAgentId
        });
        showToast("Neural sequence forged successfully");
      }
      setEditMode(false);
      fetchFlows();
    } catch (error) {
      showToast("Error synchronizing logic", "error");
    }
  };

  const handleDeleteRequest = (id) => {
    setFlowToDelete(id);
    setIsDeleteModalOpen(true);
  };
 
  const confirmDelete = async () => {
    if (!flowToDelete) return;
    setIsDeleting(true);
    try {
      await api.deleteFlow(flowToDelete);
      showToast("Flow deleted successfully", "success");
      if (activeFlow?.id === flowToDelete) setActiveFlow(null);
      fetchFlows();
      setIsDeleteModalOpen(false);
    } catch (error) {
      showToast("Error deleting flow", "error");
    } finally {
      setIsDeleting(false);
    }
  };

  const filteredFlows = flows.filter(f =>
    f.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    f.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#FDFCFD] pt-4 pb-8 px-8 lg:pt-6 lg:pb-12 lg:px-12 border-t border-gray-100">
      <div className="w-full max-w-6xl space-y-12">
        <PageHeader 
          title="Business Orchestration"
          description="Define and manage structured multi-step organizational logic. Orchestrate the neural workflows that govern your intelligence."
          actions={
            <button 
              onClick={() => {
                setActiveFlow({ name: "", description: "", content: "", is_active: true });
                setEditMode(true);
              }}
              className="flex items-center gap-2 bg-gray-900 hover:bg-gray-800 text-white px-8 py-3 rounded-2xl font-black text-sm transition-all shadow-xl shadow-gray-200 active:scale-95"
            >
              <Plus size={18} />
              Forge New Flow
            </button>
          }
        />

        <div className="grid grid-cols-12 gap-8 animate-slideIn">
          {/* Sidebar: Flow List */}
          <div className="col-span-12 lg:col-span-4">
            <div className="bg-white rounded-[32px] border border-gray-100 shadow-xl shadow-gray-100/50 flex flex-col h-[600px] overflow-hidden">
              <div className="p-6 border-b border-gray-50">
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                  <input
                    type="text"
                    placeholder="Search workflows..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-11 pr-4 py-3 bg-gray-50/50 border border-gray-50 rounded-2xl text-sm font-medium focus:ring-4 focus:ring-gray-50 outline-none transition-all"
                  />
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-2 custom-scrollbar max-h-[500px]">
                {loading ? (
                  <div className="flex flex-col items-center justify-center h-full gap-3">
                    <div className="animate-spin rounded-full h-6 w-6 border-2 border-gray-900 border-t-transparent"></div>
                    <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Loading Neural Flows</span>
                  </div>
                ) : filteredFlows.length > 0 ? (
                  filteredFlows.slice((currentPage - 1) * FLOWS_PER_PAGE, currentPage * FLOWS_PER_PAGE).map(flow => (
                    <div
                      key={flow.id}
                      onClick={() => handleSelectFlow(flow)}
                      className={`p-4 rounded-2xl cursor-pointer transition-all border group ${activeFlow?.id === flow.id
                          ? "bg-gray-900 border-gray-900 shadow-lg shadow-gray-200"
                          : "bg-white border-transparent hover:bg-gray-50"
                        }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className={`font-black uppercase tracking-widest text-xs ${activeFlow?.id === flow.id ? "text-white" : "text-gray-900"
                          }`}>
                          {flow.name}
                        </span>
                        <ChevronRight size={14} className={activeFlow?.id === flow.id ? "text-white/50" : "text-gray-300"} />
                      </div>
                      <p className={`text-[10px] font-medium line-clamp-1 ${activeFlow?.id === flow.id ? "text-white/60" : "text-gray-400"
                        }`}>
                        {flow.description || "No description provided."}
                      </p>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-12">
                    <Layers size={32} className="mx-auto text-gray-100 mb-3" />
                    <p className="text-[10px] font-black text-gray-300 uppercase tracking-widest">No Sequences Found</p>
                  </div>
                )}
              </div>

              {/* Pagination Controls */}
              {Math.ceil(filteredFlows.length / FLOWS_PER_PAGE) > 1 && (
                <div className="flex items-center justify-between p-4 border-t border-gray-50 bg-white">
                  <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">
                    Page {currentPage} of {Math.ceil(filteredFlows.length / FLOWS_PER_PAGE)}
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className="p-1.5 rounded-lg border border-gray-100 text-gray-400 hover:text-gray-900 hover:bg-gray-50 disabled:opacity-30 disabled:hover:bg-transparent transition-all"
                    >
                      <ChevronLeft size={14} />
                    </button>
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(filteredFlows.length / FLOWS_PER_PAGE)))}
                      disabled={currentPage === Math.ceil(filteredFlows.length / FLOWS_PER_PAGE)}
                      className="p-1.5 rounded-lg border border-gray-100 text-gray-400 hover:text-gray-900 hover:bg-gray-50 disabled:opacity-30 disabled:hover:bg-transparent transition-all"
                    >
                      <ChevronRight size={14} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Main Content: Flow Editor */}
          <div className="col-span-12 lg:col-span-8">
            {activeFlow ? (
              <div className="bg-white rounded-[32px] border border-gray-100 shadow-xl shadow-gray-100/50 flex flex-col h-[600px] overflow-hidden">
                {/* Editor Header */}
                <div className="px-8 py-6 border-b border-gray-50 bg-gray-50/30 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div>
                      <h2 className="font-black text-gray-900 uppercase tracking-widest text-xs">
                        {editMode ? (activeFlow.id ? "Modify Logic" : "Create Logic") : "Neural Sequence"}
                      </h2>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => handleDeleteRequest(activeFlow.id)}
                      disabled={!activeFlow.id}
                      className="p-2.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all disabled:opacity-0"
                      title="Delete Sequence"
                    >
                      <Trash2 size={18} />
                    </button>
                    {!editMode ? (
                      <button
                        onClick={() => setEditMode(true)}
                        className="px-6 py-2.5 text-xs font-black bg-gray-100 text-gray-900 rounded-2xl hover:bg-gray-200 transition-all uppercase"
                      >
                        Edit Flow
                      </button>
                    ) : (
                      <button
                        onClick={() => setEditMode(false)}
                        className="px-6 py-2.5 text-xs font-black text-gray-400 hover:text-gray-600 transition-all uppercase"
                      >
                        Cancel
                      </button>
                    )}
                    <button
                      onClick={handleSave}
                      disabled={!editMode}
                      className={`flex items-center gap-2 px-6 py-2.5 rounded-2xl font-black text-xs transition-all shadow-xl uppercase ${editMode
                          ? "bg-gray-900 hover:bg-gray-800 text-white shadow-gray-200 active:scale-95"
                          : "bg-gray-50 text-gray-300 cursor-not-allowed shadow-none"
                        }`}
                    >
                      <Save size={16} />
                      Commit
                    </button>
                  </div>
                </div>

                {/* Editor Content */}
                <div className="flex-1 overflow-y-auto p-8 space-y-8 custom-scrollbar">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text uppercase tracking-[0.2em] ml-1">Flow Name</label>
                    <input
                      type="text"
                      disabled={!editMode}
                      value={activeFlow.name}
                      onChange={(e) => setActiveFlow({ ...activeFlow, name: e.target.value })}
                      className="w-full px-5 py-3 bg-white border border-gray-200 rounded-2xl text-xs font-medium focus:ring-4 focus:ring-gray-100 outline-none transition-all disabled:bg-gray-50 disabled:opacity-70 shadow-sm"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-[10px] font-black text uppercase tracking-[0.2em] ml-1">Description</label>
                    <input
                      type="text"
                      disabled={!editMode}
                      value={activeFlow.description}
                      onChange={(e) => setActiveFlow({ ...activeFlow, description: e.target.value })}
                      className="w-full px-5 py-3 bg-white border border-gray-200 rounded-2xl text-xs font-medium focus:ring-4 focus:ring-gray-100 outline-none transition-all disabled:bg-gray-50 disabled:opacity-70 shadow-sm"
                    />
                  </div>

                  <div className="space-y-2 flex flex-col flex-1 min-h-[300px]">
                    <label className="text-[10px] font-black text uppercase tracking-[0.2em] ml-1 flex items-center justify-between">
                      Logic Definition
                      <FileCode2 size={14} className="text-gray-300" />
                    </label>
                    <textarea
                      disabled={!editMode}
                      value={activeFlow.content}
                      onChange={(e) => setActiveFlow({ ...activeFlow, content: e.target.value })}
                      placeholder="Define steps here..."
                      className="flex-1 w-full p-6 bg-white text-gray-900 border border-gray-200 rounded-[24px] font-mono text-xs leading-relaxed focus:ring-8 focus:ring-gray-100/5 outline-none transition-all disabled:bg-gray-50 disabled:opacity-90 resize-none shadow-sm custom-scrollbar"
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-[32px] border border-gray-100 shadow-xl shadow-gray-100/50 h-[600px] flex flex-col items-center justify-center p-12 text-center">
                <div className="bg-gray-50 p-8 rounded-full text-gray-200 mb-6">
                  <GitBranch size={48} />
                </div>
                <h2 className="text-xl font-black text-gray-900 uppercase tracking-tighter">Select a Workflow</h2>
                <p className="text-gray-400 text-sm max-w-xs mx-auto leading-relaxed mt-2">
                  Choose a logic sequence from the neural repository to begin modification.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Floating Notification handled by global useToast */}
      </div>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #e2e8f0;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #cbd5e1;
        }
      `}</style>
      {/* Delete Modal */}
      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        title="Delete Neural Sequence"
        message="Are you sure you want to permanently erase this business logic? This action cannot be reversed."
        loading={isDeleting}
      />
    </div>
  );
};

export default BusinessFlows;
